import React, { useCallback, useEffect, useRef, useState } from 'react';
import { decoyKeyMatches } from '../../lib/decoy';

/**
 * A clock that keeps time.
 *
 * Same requirement as the calculator, for the same reason: a disguise where
 * nothing works is worse than none at all, because the curious person who
 * picked up the phone now knows something is wrong AND has become interested.
 * So this shows the real time, keeps real alarms, and really rings.
 *
 * The door is an alarm set to a particular minute -- e.g. 03:47 -- chosen
 * because it is a thing a person does deliberately and never by accident. Any
 * other time simply becomes an alarm, saved and listed exactly as a clock
 * would, so a wrong attempt leaves something ordinary behind rather than
 * nothing at all.
 *
 * WHY IT RINGS. Somebody checking whether this app is real sets an alarm a
 * minute ahead and waits. That is the whole test, and failing it is the one
 * way this screen gives itself away. So the ring works while the app is open.
 * It cannot work while it is closed -- a web app has no way to wake a phone --
 * and that limit is stated in the interface rather than hidden.
 *
 * Deliberately no Arcanum in here: no gold, no serif, no theme tokens. It
 * reads the app's stylesheet, and anything borrowed from it would be a tell.
 */

const ALARMS_KEY = 'arca.decoy.clock.alarms';

const readAlarms = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(ALARMS_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((a) => a && typeof a.at === 'string') : [];
  } catch (e) {
    return [];
  }
};

const writeAlarms = (list) => {
  try {
    localStorage.setItem(ALARMS_KEY, JSON.stringify(list));
  } catch (e) { /* blocked storage: the clock still works, it just forgets */ }
};

const two = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${two(d.getHours())}:${two(d.getMinutes())}`;

const DAYS = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
const MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'december'];

const INK = '#F2F2F7';
const DIM = '#8E8E93';
const BG = '#0C0C0E';
const CARD = '#1C1C1E';
const ACCENT = '#F0A030';

/** A short alarm tone, built rather than fetched: no file to serve, nothing on
 *  the network that says what this app really is. */
function ring(ctx, at) {
  for (let i = 0; i < 4; i += 1) {
    const t = at + i * 0.32;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.setValueAtTime(880, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 0.3);
  }
}

const Clock = ({ onOpen }) => {
  const [now, setNow] = useState(() => new Date());
  const [tab, setTab] = useState('alarm');
  const [alarms, setAlarms] = useState(readAlarms);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ h: 7, m: 0 });
  const [firing, setFiring] = useState(null);

  // Stopwatch. Thirty lines that make this look like a clock app rather than
  // an alarm list, which is what a second glance would otherwise notice.
  const [swRunning, setSwRunning] = useState(false);
  const [swElapsed, setSwElapsed] = useState(0);
  const swFrom = useRef(0);

  const audio = useRef(null);
  const rung = useRef('');

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!swRunning) return undefined;
    const t = setInterval(() => setSwElapsed(Date.now() - swFrom.current), 50);
    return () => clearInterval(t);
  }, [swRunning]);

  // Does any enabled alarm match this minute? Guarded by `rung` so it fires
  // once per minute rather than sixty times.
  useEffect(() => {
    const stamp = hhmm(now);
    if (rung.current === stamp || firing) return;
    const due = alarms.find((a) => a.on && a.at === stamp);
    if (!due) return;
    rung.current = stamp;
    setFiring(due);
    try {
      if (!audio.current) {
        audio.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audio.current.state === 'suspended') audio.current.resume();
      ring(audio.current, audio.current.currentTime + 0.05);
    } catch (e) { /* no sound is better than a crash */ }
  }, [now, alarms, firing]);

  const save = useCallback(() => {
    const at = `${two(draft.h)}:${two(draft.m)}`;

    // The door. Checked before the alarm is stored, so opening it leaves
    // nothing in the list for the next person to wonder about.
    if (decoyKeyMatches(at)) {
      setAdding(false);
      onOpen();
      return;
    }

    const next = [...alarms.filter((a) => a.at !== at), { at, on: true }]
      .sort((a, b) => a.at.localeCompare(b.at));
    setAlarms(next);
    writeAlarms(next);
    setAdding(false);
  }, [draft, alarms, onOpen]);

  const toggle = (at) => {
    const next = alarms.map((a) => (a.at === at ? { ...a, on: !a.on } : a));
    setAlarms(next);
    writeAlarms(next);
  };

  const remove = (at) => {
    const next = alarms.filter((a) => a.at !== at);
    setAlarms(next);
    writeAlarms(next);
  };

  const wrap = {
    position: 'fixed', inset: 0, zIndex: 200, background: BG, color: INK,
    display: 'flex', flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  };

  if (firing) {
    return (
      <div style={{ ...wrap, alignItems: 'center', justifyContent: 'center', gap: 28 }}
        data-testid="decoy-clock-ringing">
        <div style={{ fontSize: 15, letterSpacing: '0.18em', textTransform: 'uppercase', color: ACCENT }}>
          Alarm
        </div>
        <div style={{ fontSize: 'clamp(56px, 20vw, 96px)', fontWeight: 200, fontVariantNumeric: 'tabular-nums' }}>
          {firing.at}
        </div>
        <button
          type="button"
          onClick={() => setFiring(null)}
          style={{
            border: 'none', borderRadius: 999, padding: '16px 52px', fontSize: 18,
            background: ACCENT, color: '#1C1C1E', fontWeight: 500, cursor: 'pointer',
          }}
          data-testid="decoy-clock-dismiss"
        >
          Stop
        </button>
      </div>
    );
  }

  return (
    <div style={wrap} data-testid="decoy-clock">
      <div style={{ padding: '18px 20px 0', display: 'flex', gap: 22, fontSize: 17 }}>
        {[['alarm', 'Alarm'], ['stopwatch', 'Stopur']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            style={{
              background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
              color: tab === id ? INK : DIM,
              borderBottom: tab === id ? `2px solid ${ACCENT}` : '2px solid transparent',
              fontSize: 17,
            }}
            data-testid={`decoy-clock-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'alarm' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '26px 20px 22px' }}>
            <div
              style={{ fontSize: 'clamp(54px, 18vw, 82px)', fontWeight: 200, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
              data-testid="decoy-clock-now"
            >
              {hhmm(now)}
              <span style={{ fontSize: '0.34em', color: DIM, marginLeft: 8 }}>{two(now.getSeconds())}</span>
            </div>
            <div style={{ color: DIM, fontSize: 15, marginTop: 8 }}>
              {DAYS[now.getDay()]} {now.getDate()}. {MONTHS[now.getMonth()]}
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 20px 20px' }}>
            {alarms.length === 0 && !adding && (
              <p style={{ color: DIM, fontSize: 15, margin: '10px 0' }}>Ingen alarmer.</p>
            )}

            {alarms.map((a) => (
              <div
                key={a.at}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, background: CARD,
                  borderRadius: 12, padding: '14px 16px', marginBottom: 10,
                }}
              >
                <span style={{
                  flex: 1, fontSize: 30, fontWeight: 300, fontVariantNumeric: 'tabular-nums',
                  color: a.on ? INK : DIM,
                }}>
                  {a.at}
                </span>
                <button
                  type="button"
                  onClick={() => remove(a.at)}
                  aria-label={`Slet alarm ${a.at}`}
                  style={{ background: 'none', border: 'none', color: DIM, fontSize: 22, cursor: 'pointer', padding: '0 6px' }}
                >
                  ×
                </button>
                <button
                  type="button"
                  onClick={() => toggle(a.at)}
                  aria-label={`Slå alarm ${a.at} ${a.on ? 'fra' : 'til'}`}
                  style={{
                    width: 50, height: 30, borderRadius: 999, border: 'none', cursor: 'pointer',
                    background: a.on ? ACCENT : '#3A3A3C', position: 'relative', flex: 'none',
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 3, left: a.on ? 23 : 3, width: 24, height: 24,
                    borderRadius: '50%', background: '#FFF', transition: 'left .15s',
                  }} />
                </button>
              </div>
            ))}

            {adding ? (
              <div style={{ background: CARD, borderRadius: 12, padding: 16 }} data-testid="decoy-clock-adding">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
                  {[['h', 23], ['m', 59]].map(([unit, max], i) => (
                    <React.Fragment key={unit}>
                      {i === 1 && <span style={{ fontSize: 34, color: DIM }}>:</span>}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, [unit]: (d[unit] + 1) % (max + 1) }))}
                          aria-label={`${unit === 'h' ? 'Time' : 'Minut'} op`}
                          style={{ background: 'none', border: 'none', color: DIM, fontSize: 20, cursor: 'pointer' }}
                        >
                          ▲
                        </button>
                        <span
                          style={{ fontSize: 40, fontWeight: 300, fontVariantNumeric: 'tabular-nums', minWidth: '1.7em', textAlign: 'center' }}
                          data-testid={`decoy-clock-${unit}`}
                        >
                          {two(draft[unit])}
                        </span>
                        <button
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, [unit]: (d[unit] + max) % (max + 1) }))}
                          aria-label={`${unit === 'h' ? 'Time' : 'Minut'} ned`}
                          style={{ background: 'none', border: 'none', color: DIM, fontSize: 20, cursor: 'pointer' }}
                        >
                          ▼
                        </button>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={save}
                    style={{ flex: 1, background: ACCENT, color: '#1C1C1E', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 16, cursor: 'pointer' }}
                    data-testid="decoy-clock-save"
                  >
                    Gem
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdding(false)}
                    style={{ flex: 1, background: '#2C2C2E', color: INK, border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 16, cursor: 'pointer' }}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setDraft({ h: now.getHours(), m: now.getMinutes() }); setAdding(true); }}
                style={{
                  width: '100%', background: CARD, color: ACCENT, border: 'none',
                  borderRadius: 12, padding: '14px 0', fontSize: 16, cursor: 'pointer',
                }}
                data-testid="decoy-clock-add"
              >
                + Ny alarm
              </button>
            )}

            {/* The honest limit, in the place a person would look for it.
                Hiding it would be the tell: an alarm that silently never rings
                is discovered later and remembered. */}
            <p style={{ color: DIM, fontSize: 12.5, lineHeight: 1.5, marginTop: 18 }}>
              Alarmer lyder kun mens appen er åben.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 34 }}>
          <div
            style={{ fontSize: 'clamp(46px, 16vw, 74px)', fontWeight: 200, fontVariantNumeric: 'tabular-nums' }}
            data-testid="decoy-clock-stopwatch"
          >
            {two(Math.floor(swElapsed / 60000))}:{two(Math.floor(swElapsed / 1000) % 60)}
            <span style={{ fontSize: '0.44em', color: DIM }}>,{two(Math.floor(swElapsed / 10) % 100)}</span>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <button
              type="button"
              onClick={() => { setSwRunning(false); setSwElapsed(0); }}
              style={{ width: 84, height: 84, borderRadius: '50%', border: 'none', background: '#2C2C2E', color: INK, fontSize: 16, cursor: 'pointer' }}
            >
              Nulstil
            </button>
            <button
              type="button"
              onClick={() => {
                if (swRunning) { setSwRunning(false); return; }
                swFrom.current = Date.now() - swElapsed;
                setSwRunning(true);
              }}
              style={{ width: 84, height: 84, borderRadius: '50%', border: 'none', background: swRunning ? '#3A2A18' : '#16301E', color: swRunning ? ACCENT : '#5BD97E', fontSize: 16, cursor: 'pointer' }}
              data-testid="decoy-clock-sw-toggle"
            >
              {swRunning ? 'Stop' : 'Start'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clock;
